"use client";
import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";

import IMAGES from "@/data/assetData";
import DateTimeCard, { roundUpToSlotBoundary, rollStaleStartForward, isBookableAt, firstBookableSlot } from "@/components/DateTimeCard/DateTimeCard";
import useCityTimings from "@/components/SearchForm/useCityTimings";

import {
  setCityname,
  getAllVehicles,
  getMobileMenuToggle,
  getTempDateTime,
  setDuration,
  setEmptyCartData,
  getStartDate,
  getEndDate,
  setSearchType,
  setSearchAreas,
  setSelectedAreas,
  setSearchBrands,
  setSelectedBrands,
  setSearchFuelTypes,
  setSelectedFuelTypes,
  setSearchTransmissions,
  setSelectedTransmissions,
  setSortBy,
} from "@/redux/search/searchSlice";
import { getFinalVehicleList, showDateTime } from "@/data/staticMethod";
import dayjs from "dayjs";
import { usefetchDataFromApi } from "@/utils/api";
import handleApiError from "@/utils/handleApiError";
import { usePathname, useRouter } from "next/navigation";
import Link from "next/link";
import { longTermDuration } from "@/data/footerLinkData";
import { bumpCartVersion } from "@/redux/order/orderSlice";
import Image from "next/image";
import { getCurrentPosition, detectCityByCoordinates, detectCityByIP } from "@/utils/locationHelper";
import { toast } from "react-toastify";
import useVehicleToggle from "../useVehicleToggle";
import VehicleTypeToggle from "../VehicleTypeToggle";
import useScrollLock from "@/hooks/useScrollLock";
import {
  usePartner,
  withPartnerFilter,
  syncPartnerBookingOrigin,
  partnerCityList,
} from "@/utils/partner/PartnerContext";

const SearchFormMobile = ({ type, seoCity = null }) => {


  const dispatch = useDispatch();
  const pathname = usePathname();
  const router = useRouter();
  // Active partner storefront (null on global sukuto.com); scopes search to the
  // partner's inventory and keeps navigation under /partner/<slug>/.
  const partner = usePartner();
  const searchSelector = useSelector((state) => state.search);

  useCityTimings();
  // Only trust the hours when they are stamped with the city we are showing.
  // Sibling effects in one commit share a render snapshot, so during a switch the
  // previous city's hours are still in scope when autoSetDates fires — that rolled
  // a 1am Bangalore pickup to 9am against Chennai's window and left it there.
  const cityTimings =
    searchSelector?.cityTimings?.cityId === searchSelector?.city?.id
      ? searchSelector.cityTimings.days
      : null;
  const { totalCartCount, isCartLoaded } = useSelector((state) => state.order);
  const [hasMounted, setHasMounted] = useState(false);

  const [cityList, setCityList] = useState([]);
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const [citySearchQuery, setCitySearchQuery] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [recentCities, setRecentCities] = useState([]);
  const cityWrapperRef = useRef(null);
  const [showSearchMobile, setShowSearchMobile] = React.useState(false);

  const [selectValidatn, setSelectedValidatn] = useState("Please select city");
  const [searchResult, setSelectedResult] = useState(false);
  const [selectedStartDate, setSelectedStartDate] = useState(null);

  const [selectedEndDate, setSelectedEndDate] = useState(null);

  const [selectedStartTime, setSelectedStartTime] = useState(null);

  const [selectedEndTime, setSelectedEndTime] = useState(null);

  const [scrolltopdata, setscrolltopdata] = useState("");
  // Mobile search pill switches to position:fixed on scroll; we reserve its
  // in-flow height with a spacer so leaving/re-entering flow doesn't shift the
  // hero (CLS). heroPillH = the pill's natural in-flow height, measured once.
  const heroPillRef = useRef(null);
  const [heroPillH, setHeroPillH] = useState(0);
  const [isStartPickerOpen, setIsStartPickerOpen] = useState(false);
  const [isEndPickerOpen, setIsEndPickerOpen] = useState(false);
  const { selectedItems, toggleSelection } = useVehicleToggle({ syncToRedux: false });
  const [showError, setShowError] = useState(false);
  const [selectedCity, setSelectedCity] = useState("Select City");

  const [selectedDuration, setSelectedDuration] = useState("Select Duration");

  const [showDurationList, setShowDurationList] = useState(false);


  const [isDetectingLocation, setIsDetectingLocation] = useState(false);
  const [locationMessage, setLocationMessage] = useState(null);

  // Hydration-safe: only read persisted Redux values after mount
  const cityName = hasMounted ? (searchSelector.city?.name || "Select City") : "Select City";

  // Sync persisted Redux state after hydration to avoid mismatch (mount-only)
  useEffect(() => {
    setHasMounted(true);
    if (searchSelector.city?.name) setSelectedCity(searchSelector.city.name);
    if (searchSelector.duration?.title) setSelectedDuration(searchSelector.duration.title);
    if (searchSelector.startDateTime?.date) setSelectedStartDate(searchSelector.startDateTime.date);
    if (searchSelector.endDateTime?.date) setSelectedEndDate(searchSelector.endDateTime.date);
    if (searchSelector.startDateTime?.time) setSelectedStartTime(searchSelector.startDateTime.time);
    if (searchSelector.endDateTime?.time) setSelectedEndTime(searchSelector.endDateTime.time);
    try {
      const recent = JSON.parse(localStorage.getItem('recentCities') || '[]');
      setRecentCities(recent);
    } catch {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep local end date in sync when Redux endDateTime changes (e.g. Quick Duration tabs)
  useEffect(() => {
    if (!hasMounted) return;
    const reduxEndDate = searchSelector.endDateTime?.date;
    const reduxEndTime = searchSelector.endDateTime?.time;
    if (reduxEndDate && reduxEndDate !== selectedEndDate) {
      setSelectedEndDate(reduxEndDate);
    }
    if (reduxEndTime && reduxEndTime !== selectedEndTime) {
      setSelectedEndTime(reduxEndTime);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchSelector.endDateTime?.date, searchSelector.endDateTime?.time]);

  const autoSetDates = useCallback(() => {
    const start = roundUpToSlotBoundary(new Date());
    const end = new Date(start.getTime() + 24 * 60 * 60 * 1000);
    const startDateStr = dayjs(start).format("YYYY-MM-DD");
    const startTimeStr = `${String(start.getHours()).padStart(2, '0')}:${String(start.getMinutes()).padStart(2, '0')}`;
    const endDateStr = dayjs(end).format("YYYY-MM-DD");
    const endTimeStr = `${String(end.getHours()).padStart(2, '0')}:${String(end.getMinutes()).padStart(2, '0')}`;
    setSelectedStartDate(startDateStr);
    setSelectedStartTime(startTimeStr);
    setSelectedEndDate(endDateStr);
    setSelectedEndTime(endTimeStr);
    dispatch(getStartDate({ date: startDateStr, time: startTimeStr }));
    dispatch(getEndDate({ date: endDateStr, time: endTimeStr }));
  }, [dispatch]);

  const dropoffMinDate = useMemo(() => {
    if (selectedStartDate && selectedStartTime) {
      const d = new Date(`${selectedStartDate}T${selectedStartTime}`);
      d.setHours(d.getHours() + 1);
      return d;
    }
    if (selectedStartDate) {
      const d = new Date(selectedStartDate);
      d.setHours(d.getHours() + 1);
      return d;
    }
    return new Date();
  }, [selectedStartDate, selectedStartTime]);

  const maxDate365 = useMemo(() => new Date(Date.now() + 365 * 24 * 60 * 60 * 1000), []);

  // The pickup this effect last saw. Moving the pickup past the drop-off used to
  // collapse the booking to the 1-hour minimum, silently turning a 2-day rental
  // into an hour; measuring against the previous pickup keeps the length instead.
  const prevStartRef = useRef(null);

  // A pickup outside the city's opening hours sends the customer to a search page
  // with nothing bookable — Chennai closes at 21:00, so an 11pm visitor was being
  // pre-filled with 11pm. Once the city's real hours land, move the pickup to the
  // first slot that city can serve and carry the drop-off with it so the chosen
  // duration survives. Fires only when the current pickup is genuinely unbookable.
  useEffect(() => {
    if (!cityTimings) return;
    if (!selectedStartDate || !selectedStartTime) return;
    if (!selectedEndDate || !selectedEndTime) return;

    const rolled = rollStaleStartForward(
      { date: selectedStartDate, time: selectedStartTime },
      { date: selectedEndDate, time: selectedEndTime },
      cityTimings
    );
    if (!rolled) return;

    setSelectedStartDate(rolled.startDate);
    setSelectedStartTime(rolled.startTime);
    setSelectedEndDate(rolled.endDate);
    setSelectedEndTime(rolled.endTime);
    dispatch(getStartDate({ date: rolled.startDate, time: rolled.startTime }));
    dispatch(getEndDate({ date: rolled.endDate, time: rolled.endTime }));
    toast.info(`Pickup moved to ${rolled.label} — the first time partners here are open`);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cityTimings, selectedStartDate, selectedStartTime]);

  useEffect(() => {
    if (!selectedStartDate || !selectedStartTime) return;
    const startDT = new Date(`${selectedStartDate}T${selectedStartTime}`);
    if (Number.isNaN(startDT.getTime())) return;
    const prevStart = prevStartRef.current;
    prevStartRef.current = startDT;

    if (!selectedEndDate || !selectedEndTime) return;
    const endDT = new Date(`${selectedEndDate}T${selectedEndTime}`);
    if (Number.isNaN(endDT.getTime())) return;
    if (endDT > startDT) return;

    const ONE_HOUR = 60 * 60 * 1000;
    const held = prevStart && endDT > prevStart ? endDT.getTime() - prevStart.getTime() : 0;
    let adjusted = new Date(startDT.getTime() + Math.max(held, ONE_HOUR));
    // Never push the drop-off past the window the picker itself allows.
    if (adjusted > maxDate365) adjusted = new Date(startDT.getTime() + ONE_HOUR);
    const adjDateStr = dayjs(adjusted).format("YYYY-MM-DD");
    const adjTimeStr = `${String(adjusted.getHours()).padStart(2, '0')}:${String(adjusted.getMinutes()).padStart(2, '0')}`;
    setSelectedEndDate(adjDateStr);
    setSelectedEndTime(adjTimeStr);
    dispatch(getEndDate({ date: adjDateStr, time: adjTimeStr }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStartDate, selectedStartTime]);

  const highlightMatch = (text, query) => {
    if (!query) return text;
    const idx = text.toLowerCase().indexOf(query.toLowerCase());
    if (idx === -1) return text;
    return (
      <>
        {text.slice(0, idx)}
        <strong className="text-primary font-bold">{text.slice(idx, idx + query.length)}</strong>
        {text.slice(idx + query.length)}
      </>
    );
  };

  const getFilteredCities = useCallback(() => {
    return cityList?.filter((city) =>
      city.name.toLowerCase().includes(citySearchQuery.toLowerCase())
    ) || [];
  }, [cityList, citySearchQuery]);

  const handleDropdownKeyDown = useCallback((e) => {
    const filtered = getFilteredCities();
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((prev) => Math.min(filtered.length - 1, prev + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((prev) => Math.max(0, prev - 1));
    } else if (e.key === 'Enter' && highlightedIndex >= 0 && highlightedIndex < filtered.length) {
      e.preventDefault();
      setCitySearchQuery("");
      setHighlightedIndex(-1);
      setShowCityDropdown(false);
      handleCitySelection(filtered[highlightedIndex]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setShowCityDropdown(false);
      setCitySearchQuery("");
      setHighlightedIndex(-1);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getFilteredCities, highlightedIndex]);

  useEffect(() => {
    if (highlightedIndex >= 0 && cityWrapperRef.current) {
      const items = cityWrapperRef.current.querySelectorAll('[data-city-option]');
      if (items[highlightedIndex]) {
        items[highlightedIndex].scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex]);

  useEffect(() => {
    setHighlightedIndex(-1);
  }, [citySearchQuery]);

  const handleSearch = useCallback(() => {
    const { startDateTime, endDateTime, vehicleType, city } = searchSelector;
    setShowError(false);
    setSelectedValidatn("");
    let startDTime = `${startDateTime.date} ${startDateTime.time}`;
    let endDTime = `${endDateTime.date} ${endDateTime.time}`;

    const rolled = rollStaleStartForward(startDateTime, endDateTime, cityTimings);
    if (rolled) {
      ({ startDTime, endDTime } = rolled);
      setSelectedStartDate(rolled.startDate);
      setSelectedStartTime(rolled.startTime);
      setSelectedEndDate(rolled.endDate);
      setSelectedEndTime(rolled.endTime);
      dispatch(getStartDate({ date: rolled.startDate, time: rolled.startTime }));
      dispatch(getEndDate({ date: rolled.endDate, time: rolled.endTime }));
      toast.info(`Pickup moved to ${rolled.label} — the earlier slot is no longer available`);
    }

    if (
      startDateTime?.date != "" &&
      startDateTime?.time != "" &&
      endDateTime?.date != "" &&
      endDateTime?.time != "" &&
      city?.name != "" &&
      vehicleType?.length != 0
    ) {
      setShowError(false);
      setSelectedResult(true);
      // Persist the checkout origin (partner cookie on a storefront, cleared on
      // the global site) so the later /cart booking is attributed correctly.
      syncPartnerBookingOrigin(partner);
      // Strip the "bike" umbrella label, matching the desktop form and the
      // /search page (navigation.jsx). Mobile was the lone outlier still
      // sending it; "bike" is the parent toggle, not a real vehicle type (the
      // actual two-wheeler types are motorbike/scooter/electric*), so removing
      // it does not change results — and it makes this request byte-identical
      // to what /search re-runs on mount, so the fetch-layer dedup in
      // usefetchDataFromApi collapses that duplicate (the ~1.44x web
      // double-count) instead of both requests hitting Elasticsearch.
      const mobileApiData = {
        types: (searchSelector.vehicleType || []).filter((t) => t != "bike"),
        start: startDTime,
        end: endDTime,
        cityId: searchSelector.city.id,
        platform: 3,
      };
      usefetchDataFromApi({
        url: "search/index",
        method: "POST",
        data: withPartnerFilter(mobileApiData, partner?.id),
      })
        .then((res) => {
          if (res.success) {
            const { vehicles, filters } = res.data?.data || {};
            dispatch(
              getTempDateTime({
                sDateTime: startDTime,
                eDateTime: endDTime,
                tCityId: city.id,
              })
            );
            let finalVehicleList = getFinalVehicleList(
              vehicles,
              startDTime,
              endDTime
            );
            dispatch(getAllVehicles(finalVehicleList));
            // Every facet list, not just areas — a search into a different city or
            // vehicle type leaves the brand/fuel/transmission options of the previous
            // one on screen otherwise.
            dispatch(setSearchAreas(filters?.areas || []));
            dispatch(setSearchBrands(filters?.brands || []));
            dispatch(setSearchFuelTypes(filters?.fuelTypes || []));
            dispatch(setSearchTransmissions(filters?.transmissions || []));
            // ...and every selection. This request carries no filters or sort, so any
            // selection left in the store would show as an active pill over results
            // that ignore it.
            dispatch(setSelectedAreas([]));
            dispatch(setSelectedBrands([]));
            dispatch(setSelectedFuelTypes([]));
            dispatch(setSelectedTransmissions([]));
            dispatch(setSortBy(""));
            setSelectedResult(false);
            if (finalVehicleList.length == 0) {
              // no vehicles found — stay on current page
            } else {
              if (pathname != "/search") {
                dispatch(setSearchType(pathname));
              }
              setSelectedResult(false);
              setShowSearchMobile(false);
              handleBMenuToggle(false);
              router.push(
                partner?.slug ? `/partner/${partner.slug}/search` : "/search"
              );
            }
          } else {
            setShowError(true);
            setSelectedResult(false);
            setSelectedValidatn(res.message);
          }
        })
        .catch((err) => {
          setSelectedValidatn(handleApiError(err));
        });
    } else {
      if (vehicleType.length == 0) {
        setSelectedValidatn("Please choose Vehicle type.");
      } else if (city?.name == "") {
        setSelectedValidatn("Please select City.");
      } else if (startDateTime?.date == "" || startDateTime?.time == "") {
        setSelectedValidatn("Please select Start Date & Time.");
      } else if (endDateTime?.date == "" || endDateTime?.time == "") {
        setSelectedValidatn("Please select End Date & Time.");
      }
      setShowError(true);
    }
  }, [dispatch, pathname, router, searchSelector, partner]);



  const handleEmptyCart = useCallback(() => {
    usefetchDataFromApi({
      url: "cart/cart-empty",
      method: "POST",
    })
      .then((res) => {
        if (res.success) {
          dispatch(bumpCartVersion());
          dispatch(
            getTempDateTime({
              sDateTime: "",
              eDateTime: "",
              tCityId: "",
            })
          );
          handleSearch();
        }
      })
      .catch(() => {});
  }, [dispatch, handleSearch]);

  // Partner-storefront guard shared by mount-time detection and the locate
  // button: global IP/GPS detection can select — or list — cities the partner
  // does not serve, stranding the customer in an empty scoped search. A
  // single-city partner gets that city preselected; multi-city opens the
  // scoped picker. Returns true when the partner scope handled selection.
  const applyPartnerCityScope = useCallback(() => {
    const scoped = partnerCityList(partner);
    if (!scoped || !scoped.length) return false;
    const currentId = searchSelector.city?.id;
    if (currentId && scoped.some((c) => String(c.id) === String(currentId))) {
      return true;
    }
    if (scoped.length === 1) {
      setSelectedCity(scoped[0]);
      dispatch(setCityname({ id: scoped[0].id, name: scoped[0].name }));
      autoSetDates();
    } else {
      setCityList(scoped);
      setShowCityDropdown(true);
    }
    return true;
  }, [partner, searchSelector, dispatch, autoSetDates]);

  const handleDetectLocation = useCallback(async () => {
    if (applyPartnerCityScope()) return;
    setIsDetectingLocation(true);
    setLocationMessage(null);
    setShowCityDropdown(false);

    try {
      const filters = {};

      if (pathname === "/monthly-bike-rental") {
        filters.longRentStatus = 1;
      }

      try {
        const coords = await getCurrentPosition();
        const response = await detectCityByCoordinates(coords.lat, coords.lng, filters);

        if (response.status === 1 && response.data) {
          setSelectedCity(response.data);
          dispatch(setCityname({ id: response.data.id, name: response.data.name }));
          setLocationMessage(null);
          toast.success('Location detected successfully');
          autoSetDates();
          setIsDetectingLocation(false);
          return;
        }
      } catch (gpsError) {
        if (gpsError.message.includes('denied')) {

          let apiUrl = "";
          if (pathname === "/monthly-bike-rental") {
            apiUrl = "data/cities?longRentStatus=1";
          } else {
            apiUrl = "data/cities";
          }

          try {
            const cityResponse = await usefetchDataFromApi({
              url: apiUrl,
              method: "POST",
            });

            if (cityResponse.success) {
              setCityList(cityResponse.data?.data);
              setShowCityDropdown(true);
            }
          } catch (err) {}

          setIsDetectingLocation(false);
          return;
        }
      }

      const ipResponse = await detectCityByIP(filters);

      if (ipResponse.status === 1 && ipResponse.data) {
        setSelectedCity(ipResponse.data);
        dispatch(setCityname({ id: ipResponse.data.id, name: ipResponse.data.name }));
        setLocationMessage(null);
        toast.info('Location detected from IP (approximate)');
        autoSetDates();
      } else {
        setLocationMessage('⚠️ Please select your city');
        toast.warning('Unable to detect location. Please select your city.');

        let apiUrl = "";
        if (pathname === "/monthly-bike-rental") {
          apiUrl = "data/cities?longRentStatus=1";
        } else {
          apiUrl = "data/cities";
        }

        try {
          const cityResponse = await usefetchDataFromApi({
            url: apiUrl,
            method: "POST",
          });

          if (cityResponse.success) {
            setCityList(cityResponse.data?.data);
            setShowCityDropdown(true);
          }
        } catch (err) {}
      }
    } catch (error) {
      setLocationMessage('⚠️ Please select your city');
      toast.error('Failed to detect location. Please select your city.');

      let apiUrl = "";
      if (pathname === "/monthly-bike-rental") {
        apiUrl = "data/cities?longRentStatus=1";
      } else {
        apiUrl = "data/cities";
      }

      try {
        const cityResponse = await usefetchDataFromApi({
          url: apiUrl,
          method: "POST",
        });

        if (cityResponse.success) {
          setCityList(cityResponse.data?.data);
          setShowCityDropdown(true);
        }
      } catch (err) {}
    } finally {
      setIsDetectingLocation(false);
    }
  }, [pathname, dispatch, autoSetDates, applyPartnerCityScope]);

  // City detection on mount, in two phases:
  //   1. Silent IP detection — no permission prompt, runs immediately.
  //   2. GPS fallback — only if IP fails. Deferred until `window.load` (full
  //      page-load complete) + an idle window, so the prompt doesn't compete
  //      with hydration or LCP. This will trip Lighthouse's "geolocation on
  //      page load" Best Practices audit — that is an accepted trade-off for
  //      automatic city detection.
  useEffect(() => {
    // On a city/locality SEO page, pre-fill that page's city (intent match) and
    // skip geo detection entirely. Backend-verified id mapping is resolved in
    // HeroSection. Not applied to /monthly-bike-rental* (its own longRent flow).
    if (seoCity?.id && !(pathname || "").includes("monthly")) {
      if (searchSelector.city?.id !== seoCity.id) {
        dispatch(setCityname({ id: seoCity.id, name: seoCity.name }));
        autoSetDates();
      }
      return;
    }

    // Partner storefront: the scope decides the city; skip geo-detection.
    if (applyPartnerCityScope()) return;

    if (searchSelector.city?.id && searchSelector.city?.name !== "Select City") return;

    let cancelled = false;
    let idleHandle = null;
    let timeoutHandle = null;
    let loadListener = null;

    const fireGps = () => {
      if (cancelled) return;
      handleDetectLocation();
    };

    const scheduleAfterIdle = () => {
      if (cancelled) return;
      if (typeof window.requestIdleCallback === "function") {
        idleHandle = window.requestIdleCallback(fireGps, { timeout: 1500 });
      } else {
        timeoutHandle = window.setTimeout(fireGps, 600);
      }
    };

    const scheduleAfterLoad = () => {
      if (document.readyState === "complete") {
        scheduleAfterIdle();
      } else {
        loadListener = () => scheduleAfterIdle();
        window.addEventListener("load", loadListener, { once: true });
      }
    };

    const tryIpThenScheduleGps = async () => {
      try {
        setIsDetectingLocation(true);
        const filters = {};
        if (pathname === "/monthly-bike-rental") filters.longRentStatus = 1;
        const ipResponse = await detectCityByIP(filters);
        if (cancelled) return;
        if (ipResponse.status === 1 && ipResponse.data) {
          dispatch(setCityname({ id: ipResponse.data.id, name: ipResponse.data.name }));
          return;
        }
      } catch (e) {
        // fall through to GPS-after-load
      } finally {
        if (!cancelled) setIsDetectingLocation(false);
      }
      if (!cancelled) scheduleAfterLoad();
    };

    tryIpThenScheduleGps();

    return () => {
      cancelled = true;
      if (loadListener) window.removeEventListener("load", loadListener);
      if (idleHandle != null && typeof window.cancelIdleCallback === "function") {
        window.cancelIdleCallback(idleHandle);
      }
      if (timeoutHandle != null) window.clearTimeout(timeoutHandle);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCityList = useCallback(() => {
    setShowError(false);
    setSelectedValidatn("");
    setIsStartPickerOpen(false);
    setIsEndPickerOpen(false);
    // On a partner storefront the picker is scoped to the partner's operating
    // cities (carried on the partner profile), so skip the global data/cities
    // fetch entirely. Off-storefront (partner null) this is a no-op.
    const scopedCities = partnerCityList(partner);
    if (scopedCities) {
      if (scopedCities.length === 0) {
        setShowCityDropdown(false);
        setShowError(true);
        setSelectedValidatn("City List not found.");
        return;
      }
      setShowCityDropdown((prev) => !prev);
      setCityList(scopedCities);
      return;
    }
    let apiUrl = "";
    if (pathname == "/monthly-bike-rental") {
      apiUrl = "data/cities?longRentStatus=1";
    } else {
      apiUrl = "data/cities";
    }
    usefetchDataFromApi({
      url: apiUrl,
      method: "POST",
    })
      .then((res) => {
        if (res.success) {
          const cities = res.data?.data;
          if (cities?.length == 0) {
            setShowCityDropdown(false);
            setShowError(true);
            setSelectedValidatn("City List not found.");
          }
          setShowCityDropdown((prev) => !prev);
          setCityList(cities);
        } else {
          setShowCityDropdown(false);
          setShowError(true);
          setSelectedValidatn("City List not found.");
        }
      })
      .catch((err) => {
        setShowCityDropdown(false);
        setShowError(true);
        setSelectedValidatn(handleApiError(err));
      });
  }, [pathname, partner]);

  const handleEmptySearch = useCallback(() => {
    const {
      startDateTime,
      endDateTime,
      tempStartDateTime,
      tempEndDateTime,
      tempCityId,
      city,
    } = searchSelector;
    let startDTime = `${startDateTime.date} ${startDateTime.time}`;
    let endDTime = `${endDateTime.date} ${endDateTime.time}`;

    if (tempStartDateTime != "" && tempEndDateTime != "" && tempCityId != "") {
      if (
        startDTime == tempStartDateTime &&
        endDTime == tempEndDateTime &&
        city.id == tempCityId
      ) {
        handleSearch();
      } else {
        handleEmptyCart();
      }
    } else {
      handleSearch();
    }
  }, [searchSelector, handleSearch, handleEmptyCart]);

  const handleDuratnSelect = useCallback((data) => {
    setSelectedDuration(data.value);
    if (searchSelector.duration.id == data.id) {
      dispatch(
        setDuration({ id: data.id, value: data.value, title: data.title })
      );
    } else {
      dispatch(
        setDuration({ id: data.id, value: data.value, title: data.title })
      );
      dispatch(setEmptyCartData(true));
    }
    setShowDurationList((prev) => !prev);
  }, [dispatch, searchSelector]);

  const handleEndDSelect = useCallback(() => {
    const { duration } = searchSelector;
    const duratnArr = duration?.value.split(" ");
    const a = dayjs(searchSelector?.startDateTime?.date);
    const b = a.add(Number(duratnArr[0]), String(duratnArr[1]));
    if (b.format("YYYY-MM-DD") != "Invalid Date") {
      dispatch(getEndDate({ date: b.format("YYYY-MM-DD") }));
      dispatch(getEndDate({ time: searchSelector?.startDateTime?.time }));
    }
  }, [dispatch, searchSelector]);


  useEffect(() => {
    if (
      pathname == "/monthly-bike-rental" &&
      (searchSelector?.startDateTime?.date != "" ||
        searchSelector?.startDateTime?.time != "")
    ) {
      handleEndDSelect();
    }
  }, [
    pathname,
    searchSelector?.startDateTime?.date,
    searchSelector?.startDateTime?.time,
    selectedDuration,
    handleEndDSelect,
  ]);


  const handleCitySelection = (city) => {
    setSelectedCity(city);
    dispatch(setCityname({ id: city.id, name: city.name }));

    setLocationMessage(null);

    try {
      const recent = JSON.parse(localStorage.getItem('recentCities') || '[]');
      const updated = [{ id: city.id, name: city.name }, ...recent.filter(c => c.id !== city.id)].slice(0, 3);
      localStorage.setItem('recentCities', JSON.stringify(updated));
      setRecentCities(updated);
    } catch {}

    autoSetDates();
  };

  const handleBMenuToggle = (data) => {
    dispatch(getMobileMenuToggle(data));
  };

  useEffect(() => {
    if (pathname === "/search" || pathname === "/search/") return;

    let rafId = null;
    const onScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        const next = window.scrollY < 220 ? "" : "scrolled";
        setscrolltopdata((prev) => (prev === next ? prev : next));
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [pathname]);

  // Measure the pill's natural in-flow height (when not yet pinned) so the
  // spacer can hold its place once it becomes position:fixed.
  useEffect(() => {
    if (!hasMounted || scrolltopdata === "scrolled") return;
    const h = heroPillRef.current?.offsetHeight;
    if (h) setHeroPillH(h);
  }, [hasMounted, scrolltopdata]);

  // Body scroll lock while the sheet is open, plus suppression of the app's
  // floating chrome. Both halves were previously broken, and the fixes now live
  // outside this component:
  //
  //   The lock is owned by utils/scrollLock.js. This component used to save and
  //   restore the body styles itself, which worked in isolation and failed the
  //   moment a second overlay overlapped it — the lead popup snapshotted this
  //   sheet's `overflow: hidden` as the page's resting state and restored it
  //   after the /search navigation, leaving /search unscrollable. The shared
  //   lock is reference counted, so overlapping overlays can no longer clobber
  //   each other. It also keeps the iOS body-pin (Safari ignores
  //   `overflow: hidden` on <body>) without paying for it on other platforms.
  //
  //   The old chrome selectors matched NOTHING. `.mobile-bar` is not a class on
  //   MobileBar (that one is hidden via the redux `mobileSearch` flag, so it
  //   worked by luck); `#wati-widget-container` / `.wati-widget-wrapper` went
  //   away with the WATI cutover; and `[id*='whatsapp']` never matched the
  //   wa.me replacement, which had no id at all. So the WhatsApp FAB
  //   (z-index 999) and SmartAppBanner (z-index 99999) floated over the sheet.
  //   Both now carry stable ids and are hidden declaratively from globals.css
  //   via `html[data-scroll-locked]`, an attribute the shared lock sets for any
  //   modal — CSS rather than inline styles, so a re-render of either component
  //   can't clobber the suppression.
  useScrollLock(showSearchMobile);

  return (
    <>
      {pathname == "/search" || pathname == "/search/" ? (
        <div className="lg:hidden flex flex-row items-center w-full gap-1">
          <div
            className="flex-1 flex items-center bg-gray-100 rounded-card px-3 py-2 ml-3 cursor-pointer"
            onClick={() => {
              handleBMenuToggle(true);
              setShowSearchMobile(true);
            }}
          >
            <Link
              href={"/"}
              className="w-8 h-8 flex items-center justify-center shrink-0"
              onClick={(e) => e.stopPropagation()}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15 19l-7-7 7-7" stroke="rgba(0,0,0,0.87)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </Link>
            <div className="w-4" />
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-semibold text-[rgba(0,0,0,0.87)] truncate">
                {cityName || "Select City"}
              </div>
              <div className="h-[1px]" />
              <p className="text-xs text-gray-500 truncate">
                {`${showDateTime(selectedStartDate, selectedStartTime)} – ${showDateTime(selectedEndDate, selectedEndTime)}`}
              </p>
            </div>
            <div className="w-2" />
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="shrink-0">
              <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <Link
            href="/cart"
            className="w-10 h-10 flex items-center justify-center relative shrink-0 mx-1"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 22a1 1 0 100-2 1 1 0 000 2zM20 22a1 1 0 100-2 1 1 0 000 2zM1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6" stroke="rgba(0,0,0,0.87)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            {isCartLoaded && totalCartCount > 0 && (
              <span className="absolute top-0 right-0 min-w-[18px] h-[18px] flex items-center justify-center bg-primary text-white text-[10px] font-bold rounded-full px-1">
                {totalCartCount > 99 ? "99+" : totalCartCount}
              </span>
            )}
          </Link>
        </div>
      ) : (
        <>
          {scrolltopdata === "scrolled" && heroPillH > 0 && (
            <div aria-hidden className="lg:hidden w-11/12" style={{ height: heroPillH }} />
          )}
        {/* The primary mobile conversion element. Was a <div onClick> — no role, no
            tabIndex, no keyboard path, announced as static text — with a second
            onClick on the inner text block. Only that inner block opened the search
            sheet; tapping the magnifier or the padding merely dispatched the redux
            menu toggle and appeared dead. It is now one <button> whose single
            handler does both, plus an explicit "Search" affordance (desktop has had
            a labelled search button all along; mobile showed only city + dates, so
            nothing signalled the pill was tappable).

            Height is deliberately unchanged (py-2 + a 40px content column; the 36px
            Search chip fits inside it). heroPillRef must keep measuring the pill's
            in-flow height — the aria-hidden spacer above holds that exact height
            when the pill goes `fixed` on scroll, so a height change here reappears
            as a scroll jump. */}
        <button
          type="button"
          ref={heroPillRef}
          onClick={() => {
            handleBMenuToggle(true);
            setShowSearchMobile(true);
          }}
          className={`lg:hidden border-0 rounded-full pl-3 pr-1.5 py-2 w-11/12 flex bg-white items-center text-left shadow-card-sm [transition:top_ease_0.3s] -top-[10px] ${scrolltopdata === "scrolled" ? "fixed !top-[18px] pt-1.5 pb-1.5 m-0 z-[52] bg-white drop-shadow-[0px_4px_24px_rgba(255,90,95,0.10)]" : ""}`}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="shrink-0 text-primary" aria-hidden="true">
            <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2"/>
            <path d="M16.5 16.5L21 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          <span className="ml-4 flex-1 min-w-0">
            <span className="block font-semibold text-textPrimary text-[15px] truncate">
              {cityName}
            </span>
            <span className="block text-sm text-gray-500 truncate">
              {`${showDateTime(selectedStartDate, selectedStartTime)} – ${showDateTime(selectedEndDate, selectedEndTime)}`}
            </span>
          </span>
          <span className="shrink-0 ml-2 inline-flex items-center justify-center h-9 px-4 rounded-pill bg-primary text-white text-[13px] font-semibold">
            Search
          </span>
        </button>
        </>
      )}
      {/* PORTALLED TO <body> — do not move this back inline.
          The sheet used to render in place, which put it inside two ancestors
          that broke it:

            HeroSection  -> `relative overflow-hidden`  (a clip box)
            SearchPill   -> `relative z-10`             (a stacking context)

          The clip box is what physically cut off the Clear All / Search bar: the
          bar is pinned to the viewport bottom (664 on an iPhone 13) but the hero
          box ended at 622, so only the top 10px of a 40px button survived, and
          scrolling clipped progressively more until the sheet vanished.

          The stacking context is the second, older half of the bug: `z-[70]` and
          the `z-[60]` scrim were resolved INSIDE a z-10 context, so at page level
          the whole sheet sat at 10 — beneath HeaderBar (20), MobileBar (51),
          NavigationBar (100), the WhatsApp FAB (999) and SmartAppBanner (99999),
          all of which visibly drew on top of a scrim meant to cover everything.

          Portalling to <body> escapes both in one move and is the correct place
          for a modal anyway. z-indices are raised to sit above every piece of app
          chrome. Guarded on hasMounted so it never runs during SSR/hydration —
          the sheet is client-only (showSearchMobile is false on the server), so
          this changes nothing in the server-rendered HTML. */}
      {showSearchMobile && hasMounted && createPortal(
        <>
          {/* overscroll-contain: without it, flicking past the end of the sheet
              chains the gesture to the document underneath. Harmless where the
              body is `overflow: hidden`, but on iOS the lock is a position pin
              and the chained scroll is what drags the page around behind the
              sheet. */}
          <div className="px-2 overflow-x-hidden overflow-y-auto overscroll-contain fixed inset-3 z-[1010] outline-none focus:outline-none lg:pointer-events-none">
            <div className="relative w-full pointer-events-auto overflow-y-auto overscroll-contain h-[90%]">
              <div className="border-0 rounded-3xl relative flex flex-col w-full bg-transparent outline-none focus:outline-none">
                <div className="p-0 z-10">
                  <button
                    className="absolute top-2 left-2 shadow-card-sm rounded-full border bg-white p-2 text-black hover:text-gray-700"
                    onClick={() => {
                      setShowSearchMobile(false), handleBMenuToggle(false);
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      className="h-3 w-3"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
                <div className="relative flex-auto">
                  <VehicleTypeToggle
                    selectedItems={selectedItems}
                    toggleSelection={toggleSelection}
                    variant="onLight"
                    className="mt-3 mb-2.5 flex-row justify-center gap-3"
                  />

                  <div className="relative border shadow-card-sm rounded-xl p-2 bg-white mb-4">
                        <div
                          className="flex items-center justify-between border rounded-lg p-2 border-gray-300"
                          onClick={() => {
                            if (!isDetectingLocation) {
                              handleCityList();
                            }
                          }}
                        >
                          <div className="text-base font-medium text-primary">
                            {isDetectingLocation
                              ? "Detecting location..."
                              : (Object.keys(searchSelector.city?.name).length == 0
                                ? "Select City"
                                : searchSelector.city.name)
                            }
                          </div>
                          <div className="flex items-center gap-2 ml-auto">
                            {!isDetectingLocation && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDetectLocation();
                                }}
                                className="p-1 hover:bg-gray-100 rounded-full transition-colors"
                                title="Detect my location"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="h-5 w-5 text-primary"
                                  viewBox="0 0 24 24"
                                  fill="currentColor"
                                >
                                  <path d="M12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm8.94 3c-.46-4.17-3.77-7.48-7.94-7.94V1h-2v2.06C6.83 3.52 3.52 6.83 3.06 11H1v2h2.06c.46 4.17 3.77 7.48 7.94 7.94V23h2v-2.06c4.17-.46 7.48-3.77 7.94-7.94H23v-2h-2.06zM12 19c-3.87 0-7-3.13-7-7s3.13-7 7-7 7 3.13 7 7-3.13 7-7 7z"/>
                                </svg>
                              </button>
                            )}
                            <Image
                              alt="Search"
                              src={IMAGES.search_icon}
                              width={16}
                              height={16}
                              className="w-4"
                            />
                          </div>
                        </div>


                        {locationMessage && !showCityDropdown && (
                          <div className="mt-2 text-xs px-2">
                            <span className={
                              locationMessage.startsWith('✅') ? 'text-green-600' :
                              locationMessage.startsWith('ℹ️') ? 'text-orange-600' :
                              'text-red-600'
                            }>
                              {locationMessage}
                            </span>
                          </div>
                        )}
                        {showCityDropdown && (
                          <div className="relative bg-white rounded-2xl z-50 w-full py-2 shadow-dropdown animate-[dropdownFadeIn_0.2s_ease-out] origin-top mt-2" onKeyDown={handleDropdownKeyDown}>
                            {cityList?.length > 5 && (
                              <div className="px-3 pt-2 pb-1 border-b border-gray-100 mb-1">
                                <input
                                  type="text"
                                  className="w-full py-2 px-2.5 pl-8 text-[13px] font-normal text-textPrimary border border-gray-200 rounded-card outline-none bg-gray-50 bg-[url('data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2216%22%20height%3D%2216%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%23999%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Ccircle%20cx%3D%2211%22%20cy%3D%2211%22%20r%3D%228%22%2F%3E%3Cline%20x1%3D%2221%22%20y1%3D%2221%22%20x2%3D%2216.65%22%20y2%3D%2216.65%22%2F%3E%3C%2Fsvg%3E')] bg-[10px_center] bg-no-repeat placeholder:text-textPlaceholder focus:border-primary focus:shadow-ring-soft"
                                  placeholder="Search city..."
                                  value={citySearchQuery}
                                  onChange={(e) => setCitySearchQuery(e.target.value)}
                                  onClick={(e) => e.stopPropagation()}
                                  onKeyDown={handleDropdownKeyDown}
                                />
                              </div>
                            )}

                            {!citySearchQuery && recentCities.length > 0 && (
                              <>
                                <div className="px-4 pt-1.5 pb-0.5 text-[10px] font-semibold tracking-[0.06em] uppercase text-textMuted">Recent</div>
                                <div className="border-b border-gray-100 pb-1 mb-1">
                                  {recentCities
                                    .filter(rc => cityList?.some(c => c.id === rc.id))
                                    .map((city) => (
                                      <div
                                        key={`recent-${city.id}`}
                                        className={`flex items-center gap-2.5 py-2 px-4 cursor-pointer text-[13px] font-medium text-gray-500 border-l-[3px] border-transparent transition-[background-color,padding-left,border-color] duration-150 hover:bg-red-50 hover:pl-5 hover:border-l-primary ${selectedCity === city.id ? "!bg-red-50 !border-l-primary !text-primary" : ""}`}
                                        onClick={() => {
                                          setCitySearchQuery("");
                                          setHighlightedIndex(-1);
                                          setShowCityDropdown(false);
                                          handleCitySelection(city);
                                        }}
                                      >
                                        <svg className="shrink-0 w-3.5 h-3.5 text-textMuted transition-colors duration-150" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                          <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
                                        </svg>
                                        {city.name}
                                      </div>
                                    ))}
                                </div>
                              </>
                            )}

                            {(() => { const fc = getFilteredCities(); return (
                              <div className="px-4 pt-1.5 pb-0.5 text-[10px] font-semibold tracking-[0.06em] uppercase text-textMuted">
                                Popular Cities
                                <span className="ml-1.5 text-[10px] font-medium text-textMuted tracking-normal normal-case">{fc.length} {fc.length === 1 ? 'city' : 'cities'}</span>
                              </div>
                            ); })()}

                            <div className="overflow-y-auto max-h-[220px] scrollbar-thin [&::-webkit-scrollbar]:w-[5px] [&::-webkit-scrollbar-thumb]:bg-black/15 [&::-webkit-scrollbar-thumb]:rounded-card [&::-webkit-scrollbar-track]:bg-transparent" ref={cityWrapperRef}>
                              {cityList?.length === 0 ? (
                                <div className="py-1">
                                  {[...Array(5)].map((_, i) => (
                                    <div key={i} className="flex items-center gap-2.5 py-2.5 px-4">
                                      <div className="w-4 h-4 rounded-full bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 bg-[length:200px_100%] animate-[citySkeletonShimmer_1.2s_ease-in-out_infinite]" />
                                      <div className="h-3 rounded-md bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 bg-[length:200px_100%] animate-[citySkeletonShimmer_1.2s_ease-in-out_infinite]" style={{ width: `${60 + Math.random() * 40}%` }} />
                                    </div>
                                  ))}
                                </div>
                              ) : getFilteredCities().length === 0 ? (
                                <div className="py-4 text-center text-[13px] text-textPlaceholder">No cities found</div>
                              ) : (
                                getFilteredCities().map((city, index) => (
                                    <div
                                      key={index}
                                      data-city-option
                                      className={`flex items-center gap-2.5 py-2.5 px-4 cursor-pointer text-sm font-medium text-textPrimary border-l-[3px] border-transparent transition-[background-color,padding-left,border-color] duration-150 hover:bg-red-50 hover:pl-5 hover:border-l-primary ${
                                        selectedCity === city.id ? "!bg-red-50 !border-l-primary !text-primary" : ""
                                      } ${highlightedIndex === index ? "!bg-red-50 !pl-5 !border-l-primary" : ""}`}
                                      onClick={() => {
                                        setCitySearchQuery("");
                                        setHighlightedIndex(-1);
                                        setShowCityDropdown(false);
                                        handleCitySelection(city);
                                      }}
                                      onMouseEnter={() => setHighlightedIndex(index)}
                                    >
                                      <svg className={`shrink-0 w-4 h-4 transition-colors duration-150 ${selectedCity === city.id || highlightedIndex === index ? "text-primary" : "text-textPlaceholder"}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                                        <circle cx="12" cy="10" r="3" />
                                      </svg>
                                      <span className="flex-1 min-w-0">{highlightMatch(city.name, citySearchQuery)}</span>
                                      <svg className={`ml-auto shrink-0 w-4 h-4 text-primary transition-[opacity,transform] duration-150 ${selectedCity === city.id ? "opacity-100 scale-100" : "opacity-0 scale-50"}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="20 6 9 17 4 12" />
                                      </svg>
                                    </div>
                                  ))
                              )}
                            </div>
                          </div>
                        )}
                      </div>


                  {type == "long-rental" && (
                    <div className="border shadow-card-sm rounded-xl p-4 bg-white mb-4">
                      <h2 className="text-base font-medium text-black mb-1">
                        Duration
                      </h2>
                      <div
                        className="flex border rounded-lg p-2 border-gray-300"
                        onClick={() => setShowDurationList(!showDurationList)}
                      >
                        <Image
                          alt="Search"
                          src={IMAGES.search_icon}
                          width={16}
                          height={16}
                          className="w-4 mr-2"
                        />
                        <div>
                          {Object.keys(searchSelector.duration?.value).length ==
                          0
                            ? "Duration"
                            : searchSelector.duration?.title}
                        </div>
                      </div>
                      {showDurationList && (
                        <div className="relative bg-white rounded-2xl z-50 w-full py-2 shadow-dropdown animate-[dropdownFadeIn_0.2s_ease-out] origin-top mt-2">
                          <div className="overflow-y-auto max-h-[220px] scrollbar-thin [&::-webkit-scrollbar]:w-[5px] [&::-webkit-scrollbar-thumb]:bg-black/15 [&::-webkit-scrollbar-thumb]:rounded-card [&::-webkit-scrollbar-track]:bg-transparent">
                            {longTermDuration?.map((duratn, index) => (
                              <div
                                key={index}
                                className={`flex items-center gap-2.5 py-2.5 px-4 cursor-pointer text-sm font-medium text-textPrimary border-l-[3px] border-transparent transition-[background-color,padding-left,border-color] duration-150 hover:bg-red-50 hover:pl-5 hover:border-l-primary ${
                                  selectedDuration === duratn.value
                                    ? "!bg-red-50 !border-l-primary !text-primary"
                                    : ""
                                }`}
                                onClick={() => handleDuratnSelect(duratn)}
                              >
                                {duratn.title}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="start-date-mobile mb-4">
                    <div
                      onClick={() => {
                        if (!searchSelector.city?.id) {
                          toast.warning('Please select a city first');
                          return;
                        }
                        setIsStartPickerOpen(!isStartPickerOpen);
                        setIsEndPickerOpen(false);
                        setShowCityDropdown(false);
                        setCitySearchQuery("");
                        setHighlightedIndex(-1);
                      }}
                      className="border shadow-card-sm rounded-xl p-4 bg-white flex justify-between items-center"
                    >
                      <h2 className="text-base font-medium text-black">
                        Pickup
                      </h2>
                      <div className="text-base font-medium text-primary">
                        {showDateTime(selectedStartDate, selectedStartTime)}
                      </div>
                    </div>
                    {isStartPickerOpen && (
                      <DateTimeCard
                        label="Select Pickup"
                        selectedDate={selectedStartDate}
                        setSelectedDate={setSelectedStartDate}
                        selectedTime={selectedStartTime}
                        setSelectedTime={setSelectedStartTime}
                        setIsPickerOpen={setIsStartPickerOpen}
                        minDate={new Date()}
                        maxDate={maxDate365}
                        type="startDate"
                        dayWindows={cityTimings}
                        mobile
                      />
                    )}
                  </div>

                  {type != "long-rental" && (
                    <div className="start-date-mobile mb-4">
                      <div
                        onClick={() => {
                          if (!searchSelector.city?.id) {
                            toast.warning('Please select a city first');
                            return;
                          }
                          if (!selectedStartDate) {
                            toast.warning('Please select pickup date first');
                            return;
                          }
                          setIsEndPickerOpen(!isEndPickerOpen);
                          setIsStartPickerOpen(false);
                          setShowCityDropdown(false);
                          setCitySearchQuery("");
                          setHighlightedIndex(-1);
                        }}
                        className="border shadow-card-sm p-4 bg-white rounded-xl flex justify-between items-center"
                      >
                        <h2 className="text-base font-medium text-black">
                          Dropoff
                        </h2>
                        <div className="text-base font-medium text-primary">
                          {showDateTime(selectedEndDate, selectedEndTime)}
                        </div>
                      </div>
                      {isEndPickerOpen && (
                        <DateTimeCard
                          label="Select Drop Off"
                          selectedDate={selectedEndDate}
                          setSelectedDate={setSelectedEndDate}
                          selectedTime={selectedEndTime}
                          setSelectedTime={setSelectedEndTime}
                          setIsPickerOpen={setIsEndPickerOpen}
                          minDate={dropoffMinDate}
                          maxDate={maxDate365}
                          type="endDate"
                        dayWindows={cityTimings}
                          mobile
                        />
                      )}
                    </div>
                  )}

                  {showError && (
                    <p className="text-red text-sm mt-2 bg-gray-200 rounded-lg px-4 py-1 error-message">
                      {selectValidatn}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* pb clears the iOS home indicator. Without it the Search button sits
                in the system swipe-up region whenever Safari's toolbar auto-hides.
                Same env() pattern the AuthModal already uses. */}
            <div className="left-0 fixed bottom-0 w-full px-5 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] bg-white flex flex-row justify-between items-center border-t border-[#DDDDDD] pointer-events-auto z-10">
              <div
                onClick={() => {
                  setShowSearchMobile(false), handleBMenuToggle(false);
                }}
                className="font-semibold underline"
              >
                Clear All
              </div>
              <div
                onClick={() => {
                  handleEmptySearch();
                }}
                className="text-base bg-primary text-white px-4 py-2 lg:px-10 lg:py-3 rounded-full flex items-center"
              >
                {searchResult ? (
                  <Image
                    width={20}
                    height={20}
                    alt="sukuto"
                    src={IMAGES.Spinner2}
                    className="mr-3 w-5 h-5"
                    unoptimized
                  />
                ) : (
                  <Image
                    width={20}
                    height={20}
                    alt="sukuto"
                    src={IMAGES.GuideSearch}
                    className="mr-3 w-5 h-5"
                  />
                )}
                Search
              </div>
            </div>
          </div>
          <div className="opacity-95 fixed inset-0 z-[1000] bg-white/80 backdrop-blur-2xl !ml-0"></div>
        </>,
        document.body
      )}
    </>
  );
};

export default SearchFormMobile;
